import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Tabs, Tab, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, Paper, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, CircularProgress, Chip, Alert, FormControl, InputLabel, Select,
  Collapse, Menu,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import InputIcon from '@mui/icons-material/Input';
import OutputIcon from '@mui/icons-material/Output';
import { useNavigate } from 'react-router-dom';
import api, { sampleWarehouseService } from '../services/api';
import ManualWarehouseTransferDialog from '../components/ManualWarehouseTransferDialog';
import { navigateWithReturn, stockReturnLabel, stockReturnTo } from '../utils/pageBack';
import { orderRefFromRow } from '../utils/orderLabel';

export default function SampleWarehousePage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);

  // Stock
  const [stock, setStock] = useState([]);
  const [loadingStock, setLoadingStock] = useState(false);

  // Filters
  const [search, setSearch] = useState('');
  const [qtyFilter, setQtyFilter] = useState('all');
  const [sortKey, setSortKey] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  // Transfers history
  const [transfers, setTransfers] = useState([]);
  const [loadingTransfers, setLoadingTransfers] = useState(false);

  // Transfer dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [form, setForm] = useState({ product: '', quantity: '', order: '', note: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Header buttons state
  const [transferHubOpen, setTransferHubOpen] = useState(false);
  const [shortageOpen, setShortageOpen] = useState(false);
  const [shortages, setShortages] = useState([]);
  const [shortageLoading, setShortageLoading] = useState(false);
  const [refsAnchorEl, setRefsAnchorEl] = useState(null);
  const refsMenuOpen = Boolean(refsAnchorEl);
  const [infoDialog, setInfoDialog] = useState(null);

  const fetchStock = useCallback(async () => {
    setLoadingStock(true);
    try {
      const res = await api.get('/sample-warehouse/stock/');
      setStock(res.data?.results ?? res.data ?? []);
    } finally {
      setLoadingStock(false);
    }
  }, []);

  const fetchTransfers = useCallback(async () => {
    setLoadingTransfers(true);
    try {
      const res = await api.get('/sample-warehouse/transfers/');
      setTransfers(res.data?.results ?? res.data ?? []);
    } finally {
      setLoadingTransfers(false);
    }
  }, []);

  const fetchFormData = useCallback(async () => {
    const [prodRes, ordRes] = await Promise.all([
      api.get('/products/'),
      api.get('/production-orders/', { params: { status: 'completed', limit: 200 } }),
    ]);
    setProducts(prodRes.data?.results ?? prodRes.data ?? []);
    setOrders(ordRes.data?.results ?? ordRes.data ?? []);
  }, []);

  useEffect(() => { fetchStock(); }, [fetchStock]);
  useEffect(() => { if (tab === 1) fetchTransfers(); }, [tab, fetchTransfers]);

  const filteredStock = useMemo(() => {
    let data = [...stock];
    const q = search.trim().toLowerCase();
    if (q) data = data.filter((s) => (s.product_name || '').toLowerCase().includes(q));
    if (qtyFilter === 'gt0') data = data.filter((s) => s.quantity > 0);
    if (qtyFilter === 'eq0') data = data.filter((s) => s.quantity === 0);
    data.sort((a, b) => {
      const va = sortKey === 'quantity' ? a.quantity : (a.product_name || '');
      const vb = sortKey === 'quantity' ? b.quantity : (b.product_name || '');
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return data;
  }, [stock, search, qtyFilter, sortKey, sortDir]);

  const openDialog = async () => {
    setError('');
    setForm({ product: '', quantity: '', order: '', note: '' });
    await fetchFormData();
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    setError('');
    if (!form.product || !form.quantity || !form.order) {
      setError('Оберіть продукт, замовлення та вкажіть кількість');
      return;
    }
    setSaving(true);
    try {
      await api.post('/sample-warehouse/transfer/', {
        product_id: Number(form.product),
        quantity: Number(form.quantity),
        order_id: Number(form.order),
        note: form.note,
      });
      setDialogOpen(false);
      fetchStock();
      if (tab === 1) fetchTransfers();
    } catch (err) {
      setError(err.response?.data?.error || JSON.stringify(err.response?.data) || 'Помилка');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">Взірці</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant={shortageOpen ? 'contained' : 'outlined'} color="warning" startIcon={<WarningAmberIcon />}
            onClick={() => {
              if (!shortageOpen) {
                setShortageLoading(true);
                api.get('/material-shortages/').then(r => setShortages(r.data || [])).catch(() => setShortages([])).finally(() => setShortageLoading(false));
              }
              setShortageOpen(o => !o);
            }}>
            Нестача {shortages.length > 0 && !shortageOpen ? `(${shortages.length})` : ''}
          </Button>
          <Button variant="contained" color="primary" startIcon={<MenuBookIcon />} onClick={(e) => setRefsAnchorEl(e.currentTarget)}>
            Довідники
          </Button>
          <Menu anchorEl={refsAnchorEl} open={refsMenuOpen} onClose={() => setRefsAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} transformOrigin={{ vertical: 'top', horizontal: 'left' }}>
            {[
              ['/products', 'Вироби'],
              ['/raw-materials', 'Сировина'],
              ['/dyes', 'Фарби'],
              ['/packaging-materials', 'Упаковка'],
              ['/filament', 'Філамент'],
              ['/consumable-materials', 'Розхідні матеріали'],
            ].map(([path, label]) => (
              <MenuItem
                key={path}
                onClick={() => {
                  setRefsAnchorEl(null);
                  navigateWithReturn(navigate, path, stockReturnTo('samples'), stockReturnLabel('samples'));
                }}
              >
                {label}
              </MenuItem>
            ))}
          </Menu>
          <Button variant="contained" color="success" startIcon={<InputIcon />}
            onClick={() => setInfoDialog({ title: 'Прихід — Взірці', text: 'Взірці надходять зі складу готової продукції через операцію переміщення. Ручне надходження не передбачено.' })}>
            Прихід
          </Button>
          <Button variant="contained" color="error" startIcon={<OutputIcon />} onClick={openDialog}>
            Вибуття
          </Button>
          <Button variant="contained" color="warning" startIcon={<SwapHorizIcon />} onClick={() => setTransferHubOpen(true)}>
            Переміщення
          </Button>
        </Box>
      </Box>
      <Collapse in={shortageOpen}>
        <Box sx={{ mb: 3, border: '1px solid', borderColor: 'warning.main', borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ px: 2, py: 1, bgcolor: 'warning.main' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'warning.contrastText' }}>
              ⚠️ Нестача матеріалів для запланованих виробничих замовлень
            </Typography>
          </Box>
          {shortageLoading ? (
            <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress size={28} /></Box>
          ) : shortages.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="success.main" fontWeight={600}>✅ Нестач немає</Typography>
            </Box>
          ) : (
            <TableContainer><Table size="small">
              <TableHead><TableRow>
                <TableCell>Матеріал</TableCell><TableCell align="right">Потрібно</TableCell>
                <TableCell align="right">Є на складі</TableCell><TableCell align="right">Нестача</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {shortages.map((s, i) => (
                  <TableRow key={i}>
                    <TableCell>{s.material_name || s.name}</TableCell>
                    <TableCell align="right">{s.required_quantity ?? '—'}</TableCell>
                    <TableCell align="right">{s.available_quantity ?? '—'}</TableCell>
                    <TableCell align="right" sx={{ color: 'error.main', fontWeight: 600 }}>{s.shortage ?? '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table></TableContainer>
          )}
        </Box>
      </Collapse>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <TextField
          label="Пошук по виробу"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{ flex: '1 1 280px' }}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Фільтр</InputLabel>
          <Select label="Фільтр" value={qtyFilter} onChange={(e) => setQtyFilter(e.target.value)}>
            <MenuItem value="all">Усі</MenuItem>
            <MenuItem value="gt0">Тільки &gt; 0</MenuItem>
            <MenuItem value="eq0">Тільки = 0</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Сортування</InputLabel>
          <Select label="Сортування" value={sortKey} onChange={(e) => setSortKey(e.target.value)}>
            <MenuItem value="name">За назвою</MenuItem>
            <MenuItem value="quantity">За кількістю</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Напрям</InputLabel>
          <Select label="Напрям" value={sortDir} onChange={(e) => setSortDir(e.target.value)}>
            <MenuItem value="asc">↑ Зростання</MenuItem>
            <MenuItem value="desc">↓ Спадання</MenuItem>
          </Select>
        </FormControl>
        <Button size="small" variant="outlined" onClick={() => {
          setSearch(''); setQtyFilter('all'); setSortKey('name'); setSortDir('asc');
        }}>Скинути</Button>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tab label="Залишки" />
        <Tab label="Операції" />
      </Tabs>

      {tab === 0 && (
        <>
          <Alert severity="info" sx={{ mb: 2 }}>
            Взірці передаються з готової продукції. Переміщення фіксується в обох складах.
          </Alert>
          {loadingStock ? <CircularProgress /> : (
            <TableContainer component={Paper}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Продукт</TableCell>
                    <TableCell align="right">Кількість, шт</TableCell>
                    <TableCell>Оновлено</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredStock.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} align="center" sx={{ color: 'text.secondary' }}>
                        Склад взірців порожній
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredStock.map((s) => (
                      <TableRow key={s.id} hover>
                        <TableCell>{s.product_name || s.product}</TableCell>
                        <TableCell align="right">
                          <Chip size="small" label={s.quantity} color={s.quantity > 0 ? 'success' : 'default'} />
                        </TableCell>
                        <TableCell>{s.updated_at ? new Date(s.updated_at).toLocaleString('uk-UA') : '—'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {tab === 1 && (
        <>{loadingTransfers ? <CircularProgress /> : (
          <TableContainer component={Paper}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Продукт</TableCell>
                  <TableCell align="right">Кількість</TableCell>
                  <TableCell>Замовлення</TableCell>
                  <TableCell>Примітка</TableCell>
                  <TableCell>Дата</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transfers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ color: 'text.secondary' }}>
                      Немає переміщень
                    </TableCell>
                  </TableRow>
                ) : (
                  transfers.map((t) => (
                    <TableRow key={t.id} hover>
                      <TableCell>{t.product_name || t.product}</TableCell>
                      <TableCell align="right">{t.quantity}</TableCell>
                      <TableCell>{t.order_display || (t.finished_goods_shipment ? `Відвантаження #${t.finished_goods_shipment}` : '—')}</TableCell>
                      <TableCell>{t.note || '—'}</TableCell>
                      <TableCell>{t.created_at ? new Date(t.created_at).toLocaleString('uk-UA') : '—'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}</>
      )}

      <Dialog open={Boolean(infoDialog)} onClose={() => setInfoDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{infoDialog?.title}</DialogTitle>
        <DialogContent>
          <Typography sx={{ pt: 1 }}>{infoDialog?.text}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoDialog(null)}>Закрити</Button>
        </DialogActions>
      </Dialog>

      <ManualWarehouseTransferDialog
        hubOpen={transferHubOpen}
        onHubClose={() => setTransferHubOpen(false)}
        sourceWarehouseId="samples"
        formTitle="Ручне переміщення взірців"
        hubHint="Перемістіть взірці на будь-який інший склад. Рух зʼявиться в журналі; залишки оновляться одразу."
        options={stock.filter((s) => Number(s.quantity) > 0)}
        getOptionKey={(s) => String(s.product || s.id)}
        getOptionQty={(s) => Number(s.quantity) || 0}
        getOptionLabel={(s) => `${s.product_name || '—'} — ${s.quantity} шт`}
        qtyLabel="Кількість, шт"
        onSubmit={async ({ option, quantity, destination, note }) => {
          await sampleWarehouseService.transferOut({
            product_id: option.product || option.id,
            quantity: Math.round(quantity),
            destination,
            note,
          });
          await fetchStock();
          if (tab === 1) fetchTransfers();
        }}
      />

      {/* Transfer dialog (прихід зі СГП через «Вибуття») */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Відправити взірці зі СГП</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <TextField
            select
            fullWidth
            label="Продукт"
            value={form.product}
            onChange={(e) => setForm((f) => ({ ...f, product: e.target.value }))}
            sx={{ mb: 2 }}
          >
            {products.map((p) => (
              <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            fullWidth
            label="Замовлення (джерело з СГП)"
            value={form.order}
            onChange={(e) => setForm((f) => ({ ...f, order: e.target.value }))}
            sx={{ mb: 2 }}
          >
            {orders.map((o) => (
              <MenuItem key={o.id} value={o.id}>
                {o.display_name || o.order_number || orderRefFromRow({ ...o, production_order_id: o.id })} — {o.product_name || ''}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            type="number"
            label="Кількість, шт"
            value={form.quantity}
            onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))}
            inputProps={{ min: 1, step: 1 }}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Примітка"
            value={form.note}
            onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Скасувати</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Відправити'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
